import { useEffect, useCallback, useRef } from "react";

interface UseMouseWheelOptions {
  step?: number;
  enabled?: boolean;
}

/**
 * Custom hook that tracks mouse wheel events and calls callback
 * Does NOT store state locally - use external store for that
 * @param options - Configuration options
 * @param options.step - Step size for each wheel event (default: 5)
 * @param options.enabled - Enable capture phase (default: true)
 * @returns containerRef for attaching to element
 */
export function useMouseWheel(options: UseMouseWheelOptions = {}) {
  const { step = 5, enabled = true } = options;

  const containerRef = useRef<HTMLDivElement>(null);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!enabled) return;

      // Возвращаем дельту: +step или -step
      const delta = event.deltaY > 0 ? step : -step;
      return delta;
    },
    [step, enabled],
  );

  useEffect(() => {
    if (!enabled) return;

    const target = containerRef.current || window;

    const listener = (event: WheelEvent) => {
      const delta = handleWheel(event);
      // Диспатчим кастомное событие с дельтой
      const wheelEvent = new CustomEvent("mousewheel-delta", { detail: delta });
      target.dispatchEvent(wheelEvent);
      // НЕ делаем preventDefault — пусть событие идет к графикам для зума
    };

    target.addEventListener("wheel", listener, {
      passive: true,
      capture: true,
    });

    return () => {
      target.removeEventListener("wheel", listener);
    };
  }, [handleWheel, enabled]);

  return { containerRef };
}
